/**
 * ============================================================================
 * Aimer Atacado — Infinite Scroll (coleções + busca) — tema Dawn
 * ============================================================================
 *
 * Substitui a paginação tradicional por rolagem infinita, reutilizando o HTML
 * renderizado pelo Liquid (mesmos cards, preços e swatches).
 *
 * Resumo:
 *  1. Uma "sentinela" fica abaixo da grade; um IntersectionObserver dispara
 *     ~800px antes de ela entrar na viewport.
 *  2. Buscamos a PRÓXIMA página via fetch() da própria URL paginada (?page=N),
 *     preservando filtros (filter.*) e ordenação (sort_by).
 *  3. Extraímos apenas os <li> da grade do HTML retornado e anexamos à grade.
 *  4. O total de páginas vem do Liquid (paginate.pages) via data-attribute —
 *     paramos exatamente na última página, sem duplicar.
 *
 * Coleção x Busca:
 *  - COLEÇÃO: #product-grid é o próprio <ul>.
 *  - BUSCA:   #product-grid é uma <div> que envolve o <ul class="product-grid">.
 *  resolveGrid() trata os dois casos.
 *
 * Filtros/ordenação: o Dawn re-renderiza #ProductGridContainer (FacetFiltersForm).
 *  Um MutationObserver detecta a troca e re-inicializa do zero (página 1).
 *
 * A11y/SEO: a paginação nativa continua no DOM (crawlers/no-JS); só é escondida
 *  visualmente. history.replaceState mantém ?page=N; aria-live anuncia as peças.
 * ============================================================================
 */

(function () {
  'use strict';

  var PRELOAD_MARGIN = 800;

  /** Resolve o <ul> real da grade, seja o id no próprio <ul> (coleção) ou numa <div> em volta (busca). */
  function resolveGrid(el) {
    if (!el) return null;
    if (el.tagName === 'UL') return el;
    return el.querySelector('ul.product-grid') || el.querySelector('ul');
  }

  function InfiniteScrollController(root) {
    this.root = root; // <div id="AimerInfiniteScroll">
    this.gridRoot = document.getElementById('product-grid');
    this.grid = resolveGrid(this.gridRoot);

    this.currentPage = parseInt(root.dataset.currentPage, 10) || 1;
    this.totalPages = parseInt(root.dataset.totalPages, 10) || 1;

    this.loader = root.querySelector('.aimer-infinite-loader');
    this.endMessage = root.querySelector('.aimer-infinite-end');
    this.moreButton = root.querySelector('.aimer-infinite-more');
    this.sentinel = root.querySelector('.aimer-infinite-sentinel');
    this.liveRegion = root.querySelector('[data-aimer-live]');

    this.isLoading = false;
    this.reachedEnd = false;
    this.observer = null;
    this.scrollScheduled = false;

    // Chaves das peças já exibidas (handle do produto), para nunca duplicar card
    // e para detectar quando o Shopify devolve uma página repetida/vazia além do
    // máximo real (paginate.pages nem sempre bate com o último card existente).
    this.seenKeys = Object.create(null);
    this.indexExistingItems();

    this.onIntersect = this.onIntersect.bind(this);
    this.onMoreClick = this.onMoreClick.bind(this);
    this.onScroll = this.onScroll.bind(this);

    this.init();
  }

  /** Extrai uma chave única da peça a partir do link do produto (/products/handle). */
  InfiniteScrollController.prototype.getItemKey = function (li) {
    var link = li.querySelector ? li.querySelector('a[href*="/products/"]') : null;
    if (!link) return null;
    var href = link.getAttribute('href') || '';
    return href.split('?')[0].split('#')[0];
  };

  /** Registra as peças já renderizadas pelo Liquid na carga inicial. */
  InfiniteScrollController.prototype.indexExistingItems = function () {
    if (!this.grid) return;
    var items = this.grid.querySelectorAll(':scope > li');
    for (var i = 0; i < items.length; i++) {
      var key = this.getItemKey(items[i]);
      if (key) this.seenKeys[key] = true;
    }
  };

  InfiniteScrollController.prototype.init = function () {
    if (!this.grid) return;

    document.documentElement.classList.add('aimer-infinite-enabled');

    if (this.moreButton) {
      this.moreButton.addEventListener('click', this.onMoreClick);
    }

    if (this.currentPage >= this.totalPages) {
      this.finish();
      return;
    }

    if ('IntersectionObserver' in window && this.sentinel) {
      this.observer = new IntersectionObserver(this.onIntersect, {
        rootMargin: '0px 0px ' + PRELOAD_MARGIN + 'px 0px',
      });
      this.observer.observe(this.sentinel);
    } else {
      this.showMoreButton();
    }

    // Fallback de scroll/resize: complementa o IntersectionObserver. Alguns
    // contextos (webviews, navegadores de automação sem paint) não entregam os
    // callbacks do IO; o listener de scroll garante o carregamento mesmo assim.
    // A trava isLoading evita corridas com o próprio IO.
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    this.checkSentinel();
  };

  InfiniteScrollController.prototype.onIntersect = function (entries) {
    for (var i = 0; i < entries.length; i++) {
      if (entries[i].isIntersecting) {
        this.loadNextPage();
        break;
      }
    }
  };

  /** Fallback: throttle via setTimeout (roda mesmo sem paint) e checa a sentinela. */
  InfiniteScrollController.prototype.onScroll = function () {
    if (this.scrollScheduled) return;
    this.scrollScheduled = true;
    var self = this;
    window.setTimeout(function () {
      self.scrollScheduled = false;
      self.checkSentinel();
    }, 150);
  };

  InfiniteScrollController.prototype.checkSentinel = function () {
    if (this.isLoading || this.reachedEnd || !this.sentinel) return;
    var rect = this.sentinel.getBoundingClientRect();
    var viewportH = window.innerHeight || document.documentElement.clientHeight;
    if (rect.top <= viewportH + PRELOAD_MARGIN) {
      this.loadNextPage();
    }
  };

  InfiniteScrollController.prototype.onMoreClick = function () {
    this.loadNextPage();
  };

  InfiniteScrollController.prototype.buildNextUrl = function (nextPage) {
    var params = new URLSearchParams(window.location.search);
    params.set('page', String(nextPage));
    return window.location.pathname + '?' + params.toString();
  };

  InfiniteScrollController.prototype.loadNextPage = function () {
    if (this.isLoading || this.reachedEnd) return;
    if (this.currentPage >= this.totalPages) {
      this.finish();
      return;
    }

    var self = this;
    var nextPage = this.currentPage + 1;
    var url = this.buildNextUrl(nextPage);

    this.isLoading = true;
    this.showLoader();

    fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newGrid = resolveGrid(doc.getElementById('product-grid'));
        if (!newGrid) throw new Error('Grade não encontrada na página ' + nextPage);

        var newItems = newGrid.querySelectorAll(':scope > li');
        var added = self.appendItems(newItems);

        self.currentPage = nextPage;
        self.isLoading = false;
        self.hideLoader();

        // FREIO 1: página veio vazia (o Shopify devolve 200 com 0 cards quando
        // ?page passa do máximo real — paginate.pages nem sempre é confiável).
        // FREIO 2: veio conteúdo, mas só com peças já exibidas (página repetida).
        // Em qualquer um dos casos chegamos ao fim de verdade — encerramos, sem
        // depender só da comparação currentPage/totalPages.
        if (newItems.length === 0 || added === 0) {
          self.finish();
          return;
        }

        self.updateHistory(url);
        self.announce(added);

        if (self.currentPage >= self.totalPages) {
          self.finish();
        } else {
          if (self.observer && self.sentinel) {
            self.observer.unobserve(self.sentinel);
            self.observer.observe(self.sentinel);
          }
          // Se a sentinela continua perto da viewport (página curta / viewport
          // alta), encadeia a próxima leva — senão aguarda a rolagem.
          self.checkSentinel();
        }
      })
      .catch(function (error) {
        // eslint-disable-next-line no-console
        console.warn('[Aimer infinite-scroll]', error);
        self.isLoading = false;
        self.hideLoader();
        self.showMoreButton();
      });
  };

  InfiniteScrollController.prototype.appendItems = function (items) {
    var fragment = document.createDocumentFragment();
    var added = 0;
    for (var i = 0; i < items.length; i++) {
      var li = items[i];

      // Deduplicação: se a peça já está na grade, ignoramos. Garante que nenhuma
      // peça apareça duas vezes, mesmo que o Shopify repita uma página.
      var key = this.getItemKey(li);
      if (key) {
        if (this.seenKeys[key]) continue;
        this.seenKeys[key] = true;
      }

      if (li.classList) li.classList.add('scroll-trigger--cancel');
      var triggers = li.querySelectorAll ? li.querySelectorAll('.scroll-trigger') : [];
      for (var j = 0; j < triggers.length; j++) {
        triggers[j].classList.add('scroll-trigger--cancel');
      }
      fragment.appendChild(li);
      added++;
    }
    if (added) this.grid.appendChild(fragment);
    return added;
  };

  InfiniteScrollController.prototype.updateHistory = function (url) {
    try {
      window.history.replaceState({ aimerInfinitePage: this.currentPage }, '', url);
    } catch (e) {
      /* ignore */
    }
  };

  InfiniteScrollController.prototype.announce = function (count) {
    if (!this.liveRegion || !count) return;
    var termo = count === 1 ? 'nova peça carregada' : 'novas peças carregadas';
    this.liveRegion.textContent = count + ' ' + termo + '.';
  };

  InfiniteScrollController.prototype.showLoader = function () {
    if (this.loader) this.loader.hidden = false;
    if (this.moreButton) this.moreButton.hidden = true;
  };

  InfiniteScrollController.prototype.hideLoader = function () {
    if (this.loader) this.loader.hidden = true;
  };

  InfiniteScrollController.prototype.showMoreButton = function () {
    if (this.reachedEnd) return;
    if (this.moreButton) this.moreButton.hidden = false;
  };

  InfiniteScrollController.prototype.finish = function () {
    this.reachedEnd = true;
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    this.hideLoader();
    if (this.moreButton) this.moreButton.hidden = true;
    if (this.endMessage && this.totalPages > 1) {
      this.endMessage.hidden = false;
    }
  };

  InfiniteScrollController.prototype.destroy = function () {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    if (this.moreButton) {
      this.moreButton.removeEventListener('click', this.onMoreClick);
    }
  };

  var activeController = null;

  function boot() {
    var root = document.getElementById('AimerInfiniteScroll');
    if (activeController) {
      activeController.destroy();
      activeController = null;
    }
    if (root) {
      activeController = new InfiniteScrollController(root);
    }
  }

  function watchFacetRerenders() {
    var container = document.getElementById('ProductGridContainer');
    if (!container || !('MutationObserver' in window)) return;

    var scheduled = false;
    var mo = new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(function () {
        scheduled = false;
        var currentRoot = document.getElementById('AimerInfiniteScroll');
        if (!activeController || activeController.root !== currentRoot) {
          boot();
        }
      });
    });

    mo.observe(container, { childList: true, subtree: true });
  }

  function start() {
    boot();
    watchFacetRerenders();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
